import { useMemo } from "react";
import { BrokerSummary, TradingCounterPartySummary } from "../../../api/Api";
import {
  useCompletedCounterpartyOverview,
  useCounterpartyOverview
} from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import {
  useBrokerOverview,
  useCompletedBrokerOverview
} from "../../brokers/dataHooks/brokerQueries";
import { EntityKind } from "../../../utils/entityPaths";

/**
 * One row of the Mergers grids. Mergers are ordinary counterparties and brokers with isMerger
 * set, so the grid reads both books' overviews (filtered to mergers server side) and merges
 * them, like the warehouse sync page does. Counterparty ids and broker ids come from different
 * books and are not guaranteed distinct, so rows are keyed on kind plus id.
 */
export type MergerRow = {
  rowKey: string;
  kind: EntityKind;
  id: string;
  name: string;
  tradingTeam?: string;
  contactPersonTradingTeam?: string;
  urgent?: boolean;
  /** Treasury and tech are absent for brokers, which have neither lifecycle. */
  progress: {
    compliance?: string;
    legal?: string;
    risk?: string;
    creditRisk?: string;
    treasury?: string;
    finance?: string;
    tech?: string;
  };
  signOffStatus?: string | null;
  signOffCompletedAt?: string | null;
  operationalReadinessStatus?: string | null;
  createdAt?: string | null;
};

const fromCounterparty = (s: TradingCounterPartySummary): MergerRow => ({
  rowKey: `counterparty:${s.id}`,
  kind: "counterparty",
  id: s.id ?? "",
  name: s.counterpartyName ?? "",
  tradingTeam: s.tradingTeam,
  contactPersonTradingTeam: s.contactPersonTradingTeam,
  urgent: s.urgent,
  progress: {
    compliance: s.progress?.compliance,
    legal: s.progress?.legal,
    risk: s.progress?.risk,
    creditRisk: s.progress?.creditRisk,
    treasury: s.progress?.treasury,
    finance: s.progress?.finance,
    tech: s.progress?.tech
  },
  signOffStatus: s.signOffStatus,
  signOffCompletedAt: s.signOffCompletedAt,
  operationalReadinessStatus: s.operationalReadinessStatus,
  createdAt: s.createdAt
});

const fromBroker = (s: BrokerSummary): MergerRow => ({
  rowKey: `broker:${s.id}`,
  kind: "broker",
  id: s.id ?? "",
  name: s.brokerName ?? "",
  tradingTeam: s.tradingTeam,
  contactPersonTradingTeam: s.contactPersonTradingTeam,
  urgent: s.urgent,
  progress: {
    compliance: s.progress?.compliance,
    legal: s.progress?.legal,
    risk: s.progress?.risk,
    creditRisk: s.progress?.creditRisk,
    finance: s.progress?.finance
  },
  signOffStatus: s.signOffStatus,
  signOffCompletedAt: s.signOffCompletedAt,
  operationalReadinessStatus: s.operationalReadinessStatus,
  createdAt: s.createdAt
});

const useMergedRows = (
  counterparties: TradingCounterPartySummary[] | undefined,
  brokers: BrokerSummary[] | undefined
) =>
  useMemo(
    () => [
      ...(counterparties ?? []).filter((s) => s.id).map(fromCounterparty),
      ...(brokers ?? []).filter((s) => s.id).map(fromBroker)
    ],
    [counterparties, brokers]
  );

export const useActiveMergers = () => {
  const counterparties = useCounterpartyOverview("active", "only");
  const brokers = useBrokerOverview("active", "only");
  return {
    rows: useMergedRows(counterparties.data, brokers.data),
    loading:
      counterparties.isLoading ||
      counterparties.isFetching ||
      brokers.isLoading ||
      brokers.isFetching
  };
};

export const useCompletedMergers = () => {
  const counterparties = useCompletedCounterpartyOverview("only");
  const brokers = useCompletedBrokerOverview("only");
  return {
    rows: useMergedRows(counterparties.data, brokers.data),
    loading:
      counterparties.isLoading ||
      counterparties.isFetching ||
      brokers.isLoading ||
      brokers.isFetching
  };
};
